<template>
  <div class="price">
    <div class="price__panel"
         :class="{ 'price__panel--has-discount': hasPartnerDiscount }">
      <partner-discount-banner v-if="hasPartnerDiscount"
                               class="price__panel__banner"
                               :rate="partnerDiscountRate" />
      <div class="price__panel__content">
        <div class="price__panel__display">
          {{ $n(price, 'currency') }} {{ $t('base.priceType', priceTypeKey) }}
        </div>
        <p v-if="hasPartnerDiscount"
           class="price__panel__before">
          {{ $n(priceBeforeDiscount, 'currency') }} {{ $t('base.priceType', priceTypeKey) }}
        </p>
        <p class="price__panel__subtitle">
          {{ $t('comparator.offers.offer.rateGuaranteed') }}
        </p>
      </div>
    </div>
    <p v-if="isCeilingVisible" class="price__repayment">
      {{ $t('comparator.offers.offer.repaymentCeiling')}} :
      <span
        class="price__repayment__price"
        :class="isWarning">
        {{ $n(repaymentCeiling, 'currency') }}
      </span>
    </p>
  </div>
</template>

<script>
import { mapState } from 'pinia';
import { useDemandStore, useProjectStore, useServiceStore } from '@/stores';
import offersHelper from '@/utils/helpers/offers';
import PartnerDiscountBanner from './PartnerDiscountBanner.vue';

export default {
  name: 'offerPrice',
  components: {
    PartnerDiscountBanner,
  },
  props: {
    offer: {
      required: true,
      type: Object,
    },
  },
  computed: {
    ...mapState(useDemandStore, {
      demandStore: 'demand',
    }),
    ...mapState(useProjectStore, ['projectSelected']),
    ...mapState(useServiceStore, ['servicesPrice']),
    exclTax() {
      return this.demandStore.show_price_ht;
    },
    priceTypeKey() {
      return this.demandStore.priceTypeKey;
    },
    isCeilingVisible() {
      return this.projectSelected.params.ceiling && this.repaymentCeiling > 0;
    },
    price() {
      return offersHelper.getPrice(this.offer, this.exclTax);
    },
    priceBeforeDiscount() {
      return offersHelper.getPriceBeforeDiscount(this.offer, this.exclTax);
    },
    hasPartnerDiscount() {
      return offersHelper.hasPartnerDiscount(this.offer);
    },
    partnerDiscountRate() {
      return offersHelper.getPartnerDiscountRate(this.offer);
    },
    repaymentCeiling() {
      return offersHelper.getRepaymentCeiling(this.offer);
    },
    isWarning() {
      return {
        'price__repayment__price--is-warning': this.repaymentCeiling < this.price + this.servicesPrice,
      };
    },
  },
};
</script>

<style scoped lang="scss">
  .price {
    width: 100%;
    display: flex;
    flex-direction: column;
    color: var(--nxt-color-font);
    text-align: center;
    &__panel {
      display: flex;
      flex: 1;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      padding: 20px;
      border-radius: 4px;
      background-color: var(--nxt-color-light-grey);
      overflow: hidden;
      &--has-discount {
        justify-content: flex-start;
        padding: 0;
      }
      &--has-discount .price__panel__content {
        padding: 20px;
      }
      &__banner {
        width: 100%;
      }
      :deep(.partner-discount-banner.price__panel__banner) {
        display: flex;
        min-height: 26px;
        margin: 0;
        padding: 6px 10px;
        border-radius: 0;
        box-sizing: border-box;
        font-size: 11px;
        white-space: normal;
      }
      &__content {
        display: flex;
        flex: 1;
        justify-content: center;
        align-items: center;
        flex-direction: column;
        width: 100%;
        box-sizing: border-box;
      }
      &__before {
        margin: 0 0 8px;
        color: var(--nxt-color-grey);
        font-size: 18px;
        font-weight: 600;
        text-decoration: line-through;
      }
      &__display {
        font-size: 30px;
        font-weight: 700;
      }
      &__subtitle {
        margin: 0;
      }
    }
    &__repayment {
      &__price {
        &--is-warning {
          color: var(--nxt-color-warning);
        }
      }
    }
  }
</style>
